/* eslint-disable */
import React, { useState, useEffect, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import { Typography, Box, Button, ButtonGroup } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { client } from "../../../../services/axiosClient";
import { ThreeDots } from "react-loader-spinner";
import dayjs from "dayjs";
import { Chart, registerables } from "chart.js";

// Registrar todos os componentes do Chart.js
Chart.register(...registerables);

const useStyles = makeStyles((theme) => ({
  root: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  chartContainer: {
    flex: 1,
    minHeight: "250px",
    position: "relative",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
    minHeight: "200px",
  },
  noDataContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
    minHeight: "200px",
    color: theme.palette.text.secondary,
  },
  noDataIcon: {
    fontSize: "3rem",
    marginBottom: theme.spacing(1),
    opacity: 0.5,
  },
  // Estilos para os botões no CardHeader
  toggleGroup: {
    "& .MuiButton-root": {
      fontSize: "0.7rem",
      padding: "2px 8px",
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 0,
      minWidth: "40px",
      height: "24px",
      "&:first-child": {
        borderTopLeftRadius: theme.spacing(0.5),
        borderBottomLeftRadius: theme.spacing(0.5),
      },
      "&:last-child": {
        borderTopRightRadius: theme.spacing(0.5),
        borderBottomRightRadius: theme.spacing(0.5),
      },
      "&.Mui-selected": {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        "&:hover": {
          backgroundColor: theme.palette.primary.dark,
        },
      },
    },
  },
  // Estilo para o stat de Readiness no CardHeader
  readinessStat: {
    display: "flex",
    alignItems: "center",
    marginRight: theme.spacing(2),
    fontSize: "0.8rem",
  },
  readinessValue: {
    fontSize: "1.1rem",
    fontWeight: 600,
    marginRight: theme.spacing(0.5),
  },
  readinessLabel: {
    fontSize: "0.7rem",
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
}));

const ReadinessCurve = ({
  selectedSection,
  clientColor,
  isLoading: parentLoading,
  // Novas props para integração com o CardHeader
  viewMode,
  onViewModeChange,
  readinessValue,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  // Estados
  const [deliverablesData, setDeliverablesData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Cores modernas para o gráfico
  const chartColors = {
    planned: "#6366F1", // Indigo moderno
    actual: "#10B981", // Emerald vibrante
    mvp: "#F59E0B", // Amber moderno
    launch: "#8B5CF6", // Violet moderno
  };

  // Buscar dados dos deliverables
  useEffect(() => {
    if (!selectedSection) return;

    const fetchDeliverablesData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const response = await client.post(
          "/api/deliverables-readiness-data/",
          {
            section_id: selectedSection.id,
            view_mode: viewMode,
          },
        );

        if (response.data.success) {
          setDeliverablesData(response.data.data || []);
        } else {
          setError(response.data.error || "Erro ao carregar dados");
        }
      } catch (err) {
        console.error("Erro ao buscar dados de readiness:", err);
        setError("Erro de conexão");
      } finally {
        setIsLoading(false);
      }
    };

    fetchDeliverablesData();
  }, [selectedSection, viewMode]);

  // Processar dados para o gráfico
  const chartData = useMemo(() => {
    if (!deliverablesData.length) return [];

    // Agrupar por mês e calcular médias
    const monthlyData = {};

    deliverablesData.forEach((deliverable) => {
      if (deliverable.planned_start_date && deliverable.planned_end_date) {
        const startDate = dayjs(deliverable.planned_start_date);
        const endDate = dayjs(deliverable.planned_end_date);

        // Gerar pontos mensais entre start e end
        let currentDate = startDate.startOf("month");
        const endMonth = endDate.endOf("month");

        while (
          currentDate.isBefore(endMonth) ||
          currentDate.isSame(endMonth, "month")
        ) {
          const monthKey = currentDate.format("YYYY-MM");

          if (!monthlyData[monthKey]) {
            monthlyData[monthKey] = {
              month: currentDate.format("MMM/YY"),
              planned: 0,
              actual: 0,
              count: 0,
              plannedValues: [], // Array para armazenar percentuais individuais
            };
          }

          // Calcular percentual planejado para este mês
          let plannedPercent = 0;
          
          // Verificar se o mês atual está entre as datas do deliverable
          if (currentDate.isSame(startDate, "month") || 
              currentDate.isSame(endDate, "month") ||
              (currentDate.isAfter(startDate, "month") && currentDate.isBefore(endDate, "month"))) {
            
            // Calcular quantos dias do deliverable caem neste mês
            const monthStartDate = currentDate.startOf("month");
            const monthEndDate = currentDate.endOf("month");
            
            // Ajustar para as datas reais do deliverable
            const effectiveStart = startDate.isAfter(monthStartDate) ? startDate : monthStartDate;
            const effectiveEnd = endDate.isBefore(monthEndDate) ? endDate : monthEndDate;
            
            const daysInMonth = effectiveEnd.diff(effectiveStart, "day") + 1;
            const daysInDeliverable = endDate.diff(startDate, "day") + 1;
            
            // Percentual deste mês em relação ao total do deliverable
            plannedPercent = (daysInMonth / daysInDeliverable) * 100;
          }

          // Armazenar o percentual individual (não somar)
          monthlyData[monthKey].plannedValues.push(plannedPercent);
          monthlyData[monthKey].actual += deliverable.real_percentual_conclusion || 0;
          monthlyData[monthKey].count += 1;

          currentDate = currentDate.add(1, "month");
        }
      }
    });

    // Calcular médias e ordenar por mês
    return Object.values(monthlyData)
      .map((item) => ({
        month: item.month,
        // MÉDIA dos percentuais planejados (não soma)
        planned: Math.round((item.plannedValues.reduce((sum, val) => sum + val, 0) / item.count) * 100) / 100,
        // Média dos percentuais realizados
        actual: Math.round((item.actual / item.count) * 100) / 100,
      }))
      .sort((a, b) => {
        const [monthA, yearA] = a.month.split("/");
        const [monthB, yearB] = b.month.split("/");
        return dayjs(`${yearA}-${monthA}-01`).diff(
          dayjs(`${yearB}-${monthB}-01`),
        );
      });
  }, [deliverablesData]);

  // Criar/atualizar gráfico
  useEffect(() => {
    if (!chartRef.current || !chartData.length) return;

    // Destruir gráfico anterior se existir
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    const ctx = chartRef.current.getContext("2d");

    // Criar gradientes para o gráfico
    const plannedGradient = ctx.createLinearGradient(0, 0, 0, 400);
    plannedGradient.addColorStop(0, chartColors.planned + "40");
    plannedGradient.addColorStop(1, chartColors.planned + "10");

    const actualGradient = ctx.createLinearGradient(0, 0, 0, 400);
    actualGradient.addColorStop(0, chartColors.actual + "40");
    actualGradient.addColorStop(1, chartColors.actual + "10");

    chartInstance.current = new Chart(ctx, {
      type: "line",
      data: {
        labels: chartData.map((item) => item.month),
        datasets: [
          {
            label: t("whatshappening.readiness_curve.planned") || "Planejado",
            data: chartData.map((item) => item.planned),
            borderColor: chartColors.planned,
            backgroundColor: plannedGradient,
            borderWidth: 3,
            fill: true,
            tension: 0.4,
            pointBackgroundColor: "#ffffff",
            pointBorderColor: chartColors.planned,
            pointBorderWidth: 3,
            pointRadius: 6,
            pointHoverRadius: 8,
            pointHoverBorderWidth: 4,
            pointHoverBackgroundColor: chartColors.planned,
            pointHoverBorderColor: "#ffffff",
            // Sombra para o gráfico
            shadowColor: chartColors.planned + "30",
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowOffsetY: 4,
          },
          {
            label: t("whatshappening.readiness_curve.actual") || "Realizado",
            data: chartData.map((item) => item.actual),
            borderColor: chartColors.actual,
            backgroundColor: actualGradient,
            borderWidth: 3,
            fill: true,
            tension: 0.4,
            pointBackgroundColor: "#ffffff",
            pointBorderColor: chartColors.actual,
            pointBorderWidth: 3,
            pointRadius: 6,
            pointHoverRadius: 8,
            pointHoverBorderWidth: 4,
            pointHoverBackgroundColor: chartColors.actual,
            pointHoverBorderColor: "#ffffff",
            // Sombra para o gráfico
            shadowColor: chartColors.actual + "30",
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowOffsetY: 4,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: "bottom",
            labels: {
              usePointStyle: true,
              padding: 15,
              font: {
                size: 11,
                weight: "500",
                family: "'Inter', 'Segoe UI', sans-serif",
              },
              generateLabels: function(chart) {
                const datasets = chart.data.datasets;
                return datasets.map((dataset, index) => ({
                  text: dataset.label,
                  fillStyle: dataset.borderColor,
                  strokeStyle: dataset.borderColor,
                  lineWidth: 3,
                  pointStyle: "circle",
                  hidden: false,
                  index: index,
                }));
              },
            },
          },
          tooltip: {
            mode: "index",
            intersect: false,
            backgroundColor: "rgba(255, 255, 255, 0.98)",
            titleColor: "#1F2937",
            bodyColor: "#4B5563",
            borderColor: "#E5E7EB",
            borderWidth: 2,
            cornerRadius: 12,
            displayColors: true,
            padding: 16,
            titleFont: {
              size: 14,
              weight: "600",
            },
            bodyFont: {
              size: 13,
            },
            callbacks: {
              label: function (context) {
                return `${context.dataset.label}: ${context.parsed.y.toFixed(1)}%`;
              },
            },
            // Sombra no tooltip
            boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
          },
        },
        scales: {
          x: {
            grid: {
              color: "#F3F4F6",
              drawBorder: false,
              lineWidth: 1,
            },
            ticks: {
              color: "#6B7280",
              font: {
                size: 11,
                weight: "500",
                family: "'Inter', 'Segoe UI', sans-serif",
              },
              padding: 8,
            },
            border: {
              display: false,
            },
          },
          y: {
            beginAtZero: true,
            max: 100,
            grid: {
              color: "#F3F4F6",
              drawBorder: false,
              lineWidth: 1,
            },
            ticks: {
              color: "#6B7280",
              font: {
                size: 11,
                weight: "500",
                family: "'Inter', 'Segoe UI', sans-serif",
              },
              padding: 8,
              callback: function (value) {
                return value + "%";
              },
            },
            border: {
              display: false,
            },
          },
        },
        interaction: {
          mode: "nearest",
          axis: "x",
          intersect: false,
        },
        elements: {
          point: {
            hoverRadius: 8,
            hoverBorderWidth: 4,
          },
        },
        // Animações suaves
        animation: {
          duration: 1000,
          easing: "easeInOutQuart",
        },
        transitions: {
          active: {
            animation: {
              duration: 300,
            },
          },
        },
      },
    });

    // Cleanup function
    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [chartData, chartColors, t]);

  // Loading state
  if (parentLoading || isLoading) {
    return (
      <div className={classes.loadingContainer}>
        <ThreeDots color={clientColor} height={80} width={80} />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={classes.noDataContainer}>
        <Typography variant="body2" color="error">
          {error}
        </Typography>
      </div>
    );
  }

  // No data state
  if (!deliverablesData.length) {
    return (
      <div className={classes.noDataContainer}>
        <Typography variant="body2">
          {t("whatshappening.readiness_curve.no_data") ||
            "Nenhum dado disponível"}
        </Typography>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      {/* Gráfico */}
      <div className={classes.chartContainer}>
        <canvas ref={chartRef} />
      </div>
    </div>
  );
};

ReadinessCurve.propTypes = {
  selectedSection: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  }),
  clientColor: PropTypes.string,
  isLoading: PropTypes.bool,
  // Novas props
  viewMode: PropTypes.oneOf(["mvp", "launch"]),
  onViewModeChange: PropTypes.func,
  readinessValue: PropTypes.number,
};

ReadinessCurve.defaultProps = {
  selectedSection: null,
  clientColor: "#2196F3",
  isLoading: false,
  viewMode: "mvp",
  onViewModeChange: () => {},
  readinessValue: 0,
};

export default ReadinessCurve;
